import React, { useState, useEffect, useRef } from 'react';
import { fetchBranches } from '../../services/branchApi';
import axiosInstance from '../../services/axiosInstance';
import Select from 'react-select';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { LockKeyhole, Mail, MapPin, ShieldCheck, UserPlus, UserRound, X } from 'lucide-react';
import { showSuccessToast, showErrorToast, showWarnToast } from '../../utils/toast';
import { useTheme } from '../CommonComponents/ThemeProvider';
import tinycolor from 'tinycolor2';
import { fetchRoles } from '../../services/roleApi';

// Email and password validation regex helpers
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const isValidPassword = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,14}$/.test(password);

const createSelectStyles = (themeColor, hasError = false) => {
    const hoverTint = tinycolor(themeColor).setAlpha(0.08).toRgbString();

    return {
        control: (base, state) => ({
            ...base,
            minHeight: 48,
            height: 48,
            borderRadius: 16,
            borderColor: hasError ? "#fda4af" : state.isFocused ? themeColor : "#e2e8f0",
            backgroundColor: "#fff",
            boxShadow: state.isFocused
                ? `0 0 0 4px ${tinycolor(hasError ? "#ef4444" : themeColor).setAlpha(0.14).toRgbString()}`
                : "none",
            "&:hover": { borderColor: hasError ? "#f43f5e" : themeColor },
            fontSize: 14,
        }),
        valueContainer: (base) => ({
            ...base,
            minHeight: 46,
            height: 46,
            padding: "0 14px",
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        indicatorsContainer: (base) => ({
            ...base,
            minHeight: 46,
            height: 46,
        }),
        menu: (base) => ({
            ...base,
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            zIndex: 9999,
            boxShadow: "0 18px 45px rgba(15,23,42,0.16)",
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? themeColor
                : state.isFocused
                    ? hoverTint
                    : "#fff",
            color: state.isSelected ? "#fff" : "#0f172a",
            fontSize: 14,
        }),
        singleValue: (base) => ({
            ...base,
            color: "#0f172a",
        }),
        placeholder: (base) => ({
            ...base,
            color: "#94a3b8",
        }),
        indicatorSeparator: () => ({ display: "none" }),
        dropdownIndicator: (base, state) => ({
            ...base,
            color: state.isFocused ? themeColor : "#64748b",
            "&:hover": { color: themeColor },
        }),
    };
};

const AddUserModal = ({ editData, onClose, onUserAddedOrUpdated }) => {
    const { currentTheme } = useTheme();
    const primaryColor = currentTheme?.color || "var(--color-primary)";
    const primaryHoverColor = tinycolor(primaryColor).isValid()
        ? tinycolor(primaryColor).darken(10).saturate(4).toHexString()
        : "var(--color-hover)";
    // Form states
    const [username, setUsername] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [roleId, setRoleId] = useState('');
    const [password, setPassword] = useState('');
    const [retypePassword, setRetypePassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);


    // Branch (customer) selection
    const [branches, setBranches] = useState([]);
    const [branchId, setBranchId] = useState('');
    const [branchError, setBranchError] = useState('');
    const [roles, setRoles] = useState([]);

    // Password visibility toggles
    const [showPassword, setShowPassword] = useState(false);
    const [showRetypePassword, setShowRetypePassword] = useState(false);
    // Fetch branches (customers) on mount
    useEffect(() => {
        fetchBranches().then(res => {
            if (res && res.data && (Array.isArray(res.data.data) || Array.isArray(res.data))) {
                // Support both {data: [...]} and just [...]
                const branchList = Array.isArray(res.data.data) ? res.data.data : res.data;
                const activeBranches = branchList.filter((branch) => {
                    const status = String(branch?.status || "").toUpperCase();
                    return status === "ACTIVE";
                });

                if (editData?.branch_id) {
                    const currentBranch = branchList.find((branch) => String(branch.id) === String(editData.branch_id));
                    const alreadyIncluded = activeBranches.some((branch) => String(branch.id) === String(editData.branch_id));
                    setBranches(currentBranch && !alreadyIncluded ? [...activeBranches, currentBranch] : activeBranches);
                } else {
                    setBranches(activeBranches);
                }
            }
        }).catch(() => setBranches([]));

        fetchRoles()
            .then(res => {
                const list = res.data.data || [];
                setRoles(list.filter(r => r.status === 'ACTIVE'));
            })
            .catch(() => setRoles([]));
    }, [editData]);

    // Refs for debounce timers
    const usernameTimeoutRef = useRef(null);
    const emailTimeoutRef = useRef(null);

    // Cleanup debounce timers on unmount
    useEffect(() => {
        return () => {
            if (usernameTimeoutRef.current) clearTimeout(usernameTimeoutRef.current);
            if (emailTimeoutRef.current) clearTimeout(emailTimeoutRef.current);
        };
    }, []);

    // Initialize or reset form fields when editData changes
    useEffect(() => {
        if (editData) {
            setUsername(editData.username || '');
            setEmail(editData.email || '');
            setRoleId(editData.role_id || '');
            setPassword('');
            setRetypePassword('');
            setUsernameError('');
            setEmailError('');
            setBranchId(editData && editData.branch_id ? editData.branch_id : '');
        } else {
            setUsername('');
            setEmail('');
            setRoleId('');
            setPassword('');
            setRetypePassword('');
            setUsernameError('');
            setEmailError('');
            setBranchId('');
        }
    }, [editData]);

    // API check if username exists
    const checkUsernameExists = async (usernameToCheck) => {
        try {
            const response = await axiosInstance.get(
                `/admin/users/check-username?username=${encodeURIComponent(usernameToCheck)}`
            );
            return response.data.exists;
        } catch (error) {
            console.error('Error checking username existence:', error);
            return false;
        }
    };

    // API check if email exists
    const checkEmailExists = async (emailToCheck) => {
        try {
            const response = await axiosInstance.get(
                `/admin/users/check-email?email=${encodeURIComponent(emailToCheck)}`
            );
            return response.data.exists;
        } catch (error) {
            console.error('Error checking email existence:', error);
            return false;
        }
    };

    // Debounced username change handler with trimming
    const handleUsernameChange = (value) => {
        const trimmed = value.trim();
        setUsername(trimmed);
        setUsernameError('');

        if (usernameTimeoutRef.current) clearTimeout(usernameTimeoutRef.current);

        if (!trimmed) {
            setUsernameError('Username is required.');
            return;
        }

        // Skip API call if editing and username unchanged
        if (editData && editData.username === trimmed) return;

        usernameTimeoutRef.current = setTimeout(async () => {
            const exists = await checkUsernameExists(trimmed);
            if (exists) setUsernameError('Username already exists.');
        }, 500);
    };

    // Debounced email change handler with trimming
    const handleEmailChange = (value) => {
        const trimmed = value.trim();
        setEmail(trimmed);
        setEmailError('');

        if (emailTimeoutRef.current) clearTimeout(emailTimeoutRef.current);

        if (!trimmed) {
            setEmailError('Email is required.');
            return;
        }
        if (!isValidEmail(trimmed)) {
            setEmailError('Invalid email address.');
            return;
        }

        // Skip API call if editing and email unchanged
        if (editData && editData.email === trimmed) return;

        emailTimeoutRef.current = setTimeout(async () => {
            const exists = await checkEmailExists(trimmed);
            if (exists) setEmailError('Email already exists.');
        }, 500);
    };

    // Validate password fields (for submit)
    const validatePasswordFields = () => {
        if (editData) {
            // On edit, password optional but if entered must be valid and match
            if (password) {
                if (!isValidPassword(password)) {
                    showWarnToast(
                        'Password must be 6-14 characters and include uppercase, lowercase, number, and symbol.'
                    );
                    return false;
                }
                if (password !== retypePassword) {
                    showWarnToast("Passwords don't match.");
                    return false;
                }
            }
        } else {
            // On add, password required and must be valid and match
            if (!password) {
                showWarnToast('Please enter a password.');
                return false;
            }
            if (!isValidPassword(password)) {
                showWarnToast(
                    'Password must be 6-14 characters and include uppercase, lowercase, number, and symbol.'
                );
                return false;
            }
            if (password !== retypePassword) {
                showWarnToast("Passwords don't match.");
                return false;
            }
        }
        return true;
    };

    // Submit handler
    const handleSubmit = async () => {

        if (!username || !email || !roleId || !branchId) {
            showWarnToast('Please fill in all the required fields.');
            if (!branchId) setBranchError('Branch is required.');
            return;
        }

        if (usernameError || emailError) {
            showWarnToast('Please fix errors before submitting.');
            return;
        }

        if (!isValidEmail(email)) {
            showWarnToast('Invalid email address.');
            return;
        }

        if (!validatePasswordFields()) return;

        setIsSubmitting(true);

        try {
            // Check username uniqueness before submit if changed
            if (
                (!editData || (editData && username !== editData.username)) &&
                (await checkUsernameExists(username))
            ) {
                showWarnToast('Username already exists. Please choose another.');
                setIsSubmitting(false);
                return;
            }

            // Check email uniqueness before submit if changed
            if (
                (!editData || (editData && email !== editData.email)) &&
                (await checkEmailExists(email))
            ) {
                showWarnToast('Email already exists. Please choose another.');
                setIsSubmitting(false);
                return;
            }

            // token variable removed: axiosInstance handles JWT
            const method = editData ? 'put' : 'post';
            const url = editData
                ? `${process.env.REACT_APP_API_URL}/api/admin/users/${editData.id}`
                : `${process.env.REACT_APP_API_URL}/api/admin/users`;


            const payload = {
                username,
                email,
                role_id: roleId,
                branch_id: branchId,
            };
            if (password) {
                payload.password = password;
            }

            await axiosInstance({
                method,
                url,
                data: payload,
            });

            showSuccessToast(editData ? 'User updated successfully!' : 'User added successfully!');
            onUserAddedOrUpdated();
            onClose();
        } catch (error) {
            console.error('Error saving user:', error);
            const message = error.response?.data?.message || 'Something went wrong while saving the user.';
            showErrorToast(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const fieldBaseClass = "h-12 w-full rounded-2xl border bg-white px-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:ring-4 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500";
    const normalFieldClass = `${fieldBaseClass} border-slate-200 focus:border-[var(--color-primary)] focus:ring-[color-mix(in_srgb,var(--color-primary)_14%,white)]`;
    const invalidFieldClass = `${fieldBaseClass} border-rose-300 focus:border-rose-500 focus:ring-rose-100`;
    const labelClass = "mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400";
    const branchOptions = branches.map((branch) => ({
        value: String(branch.id),
        label: branch.name || branch.location || String(branch.id),
    }));
    const roleOptions = roles.map((role) => ({
        value: String(role.id),
        label: role.name,
    }));
    const selectedBranchOption = branchOptions.find((option) => option.value === String(branchId)) || null;
    const selectedRoleOption = roleOptions.find((option) => option.value === String(roleId)) || null;
    const selectStyles = createSelectStyles(primaryColor);
    const branchSelectStyles = createSelectStyles(primaryColor, Boolean(branchError));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm">
            <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-[28px] bg-white shadow-[0_28px_90px_rgba(15,23,42,0.28)]">
                <div className="shrink-0 border-b border-slate-200 bg-white/95 px-6 py-5 backdrop-blur">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: "var(--color-primary)" }}>
                                {editData ? <UserRound className="h-6 w-6" /> : <UserPlus className="h-6 w-6" />}
                            </div>
                            <div>
                                <h2 className="text-2xl font-semibold tracking-tight text-slate-950">
                                    {editData ? 'Edit User' : 'Add User'}
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-slate-500">
                                    {editData ? 'Update account access, branch assignment, and optional password.' : 'Create a staff account and assign access to a branch.'}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                            aria-label="Close user modal"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="min-h-0 space-y-4 overflow-y-auto overflow-x-hidden px-6 py-5">
                    <section className="rounded-[26px] border border-slate-200 bg-slate-50/70 p-4 shadow-inner">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className={labelClass}>
                                    <UserRound className="h-4 w-4" />
                                    Username
                                </label>
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => handleUsernameChange(e.target.value)}
                                    className={usernameError ? invalidFieldClass : normalFieldClass}
                                    placeholder="Enter username"
                                    disabled={isSubmitting}
                                />
                                {usernameError && <p className="mt-2 text-xs font-medium text-rose-500">{usernameError}</p>}
                            </div>

                            <div>
                                <label className={labelClass}>
                                    <Mail className="h-4 w-4" />
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => handleEmailChange(e.target.value)}
                                    className={emailError ? invalidFieldClass : normalFieldClass}
                                    placeholder="Enter email address"
                                    disabled={isSubmitting}
                                />
                                {emailError && <p className="mt-2 text-xs font-medium text-rose-500">{emailError}</p>}
                            </div>

                            <div>
                                <label className={labelClass}>
                                    <MapPin className="h-4 w-4" />
                                    Branch
                                </label>
                                <Select
                                    inputId="user-branch-select"
                                    options={branchOptions}
                                    onChange={(option) => {
                                        setBranchId(option?.value || '');
                                        setBranchError('');
                                    }}
                                    value={selectedBranchOption}
                                    styles={branchSelectStyles}
                                    placeholder="Select Branch"
                                    isDisabled={isSubmitting}
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                                {branchError && <p className="mt-2 text-xs font-medium text-rose-500">{branchError}</p>}
                            </div>

                            <div>
                                <label className={labelClass}>
                                    <ShieldCheck className="h-4 w-4" />
                                    Role
                                </label>
                                <Select
                                    inputId="user-role-select"
                                    options={roleOptions}
                                    value={selectedRoleOption}
                                    onChange={(option) => setRoleId(option?.value || '')}
                                    styles={selectStyles}
                                    placeholder="Select Role"
                                    isDisabled={isSubmitting}
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                        </div>
                    </section>

                    <section className="rounded-[26px] border border-slate-200 bg-white p-4 shadow-[0_14px_35px_rgba(15,23,42,0.05)]">
                        <div className="mb-4 flex items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                                <LockKeyhole className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-slate-900">Password</p>
                                <p className="mt-1 text-sm text-slate-500">
                                    {editData ? 'Leave both password fields blank to keep the current password.' : 'Use 6-14 characters with uppercase, lowercase, number, and symbol.'}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className={labelClass}>Password</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className={`${normalFieldClass} pr-12`}
                                        placeholder={editData ? 'Optional password' : 'Enter password'}
                                        disabled={isSubmitting}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Retype Password</label>
                                <div className="relative">
                                    <input
                                        type={showRetypePassword ? 'text' : 'password'}
                                        value={retypePassword}
                                        onChange={(e) => setRetypePassword(e.target.value)}
                                        className={`${normalFieldClass} pr-12`}
                                        placeholder={editData ? 'Optional confirmation' : 'Retype password'}
                                        disabled={isSubmitting}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowRetypePassword((prev) => !prev)}
                                        className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                                        aria-label={showRetypePassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showRetypePassword ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="flex flex-col-reverse gap-3 pt-1 sm:flex-row sm:justify-end">
                        <button
                            type="button"
                            className="inline-flex h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isSubmitting || usernameError || emailError}
                            className="inline-flex h-11 items-center justify-center rounded-2xl px-6 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none"
                            style={isSubmitting || usernameError || emailError ? undefined : { background: primaryColor }}
                            onMouseOver={isSubmitting || usernameError || emailError ? undefined : (event) => {
                                event.currentTarget.style.background = primaryHoverColor;
                            }}
                            onMouseOut={isSubmitting || usernameError || emailError ? undefined : (event) => {
                                event.currentTarget.style.background = primaryColor;
                            }}
                        >
                            {isSubmitting ? 'Saving...' : editData ? 'Update User' : 'Add User'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddUserModal;
